import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X } from 'lucide-react';

const INSTALL_LOCK_KEY = 'flyback_pwa_install_lock';
const INSTALL_LOCK_MS = 10000; // Reduced to 10 seconds for easier testing

let deferredPromptGlobal = typeof window !== 'undefined' ? window.deferredInstallPrompt || null : null;
let installInProgress = false;

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function isInstallLocked() {
  if (typeof window === 'undefined') return false;
  const raw = Number(window.sessionStorage.getItem(INSTALL_LOCK_KEY) || 0);
  return Number.isFinite(raw) && raw > 0 && Date.now() - raw < INSTALL_LOCK_MS;
}

function lockInstallFlow() {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(INSTALL_LOCK_KEY, String(Date.now()));
}

export default function PwaInstallPrompt() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(deferredPromptGlobal);
  const [installed, setInstalled] = useState(typeof window !== 'undefined' ? isStandalone() : false);
  const [installing, setInstalling] = useState(false);
  const [hidden, setHidden] = useState(false);

  // Show install CTA only on generic guest entry flow routes.
  const path = location.pathname;
  const isInstallEligibleRoute = 
    path === '/store' || 
    path === '/store/login' || 
    path === '/store/select' || 
    /^\/store\/[^\/]+$/.test(path) || 
    /^\/store\/[^\/]+\/login$/.test(path);

  useEffect(() => {
    if (installed) return undefined;

    const handleBeforeInstallPrompt = (e) => {
      if (isInstallLocked() || installInProgress || deferredPromptGlobal) return;

      e.preventDefault();
      deferredPromptGlobal = e;
      setDeferredPrompt(e);
      console.log('Install prompt ready');
    };

    const handleAppInstalled = () => {
      console.log('PWA installed');
      setInstalled(true);
      setHidden(true);
      setInstalling(false);
      installInProgress = false;
      deferredPromptGlobal = null;
      setDeferredPrompt(null);
      lockInstallFlow();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [installed]);

  const handleInstall = async () => {
    if (!deferredPrompt || installing || installInProgress) return;

    try {
      setInstalling(true);
      installInProgress = true;
      lockInstallFlow();
      console.log('Install clicked');

      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('User choice:', outcome);

      if (outcome !== 'accepted') {
        setInstalling(false);
        installInProgress = false;
      } else {
        // If accepted, Chrome will fire the 'appinstalled' event when done.
        // We can hide our custom prompt now since the user accepted.
        setHidden(true);
      }
    } catch (err) {
      console.error('Install error:', err);
      setInstalling(false);
      installInProgress = false;
    } finally {
      deferredPromptGlobal = null;
      setDeferredPrompt(null);
    }
  };

  if (installed || hidden || !deferredPrompt || isInstallLocked() || !isInstallEligibleRoute) return null;

  return (
    <div className="fixed bottom-24 left-1/2 z-50 w-[92%] max-w-md -translate-x-1/2 rounded-2xl border border-stone-200 bg-white/95 p-3 shadow-2xl backdrop-blur dark:border-stone-700 dark:bg-stone-900/90">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-stone-900 dark:text-white">
            Install App
          </p>
          <p className="text-xs text-stone-600 dark:text-stone-300">
            Add FlyBack Rewards for a better experience.
          </p>
        </div>

        <button
          onClick={() => setHidden(true)}
          className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <button
        onClick={handleInstall}
        disabled={installing}
        className="mt-3 w-full h-10 rounded-xl gradient-brand-btn text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <Download className="h-4 w-4" />
        {installing ? 'Installing...' : 'Install App'}
      </button>
    </div>
  );
}
